import React, { useEffect, useRef, useState } from "react";

/**
 * Reveal — gentle fade + upward motion when the element enters the viewport.
 *
 * Usage:
 *   <Reveal>...</Reveal>
 *   <Reveal as="section">...</Reveal>
 *   <Reveal stagger>...</Reveal>  // children animate with staggered delay
 *
 * Respects prefers-reduced-motion via the .reveal class CSS in index.css.
 */
const Reveal = ({
  children,
  as: Tag = "div",
  className = "",
  stagger = false,
  threshold = 0.12,
  rootMargin = "0px 0px -40px 0px",
  ...rest
}) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            obs.unobserve(entry.target);
          }
        });
      },
      { threshold, rootMargin }
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, [threshold, rootMargin]);

  // For staggered children, mark each child with .reveal + is-visible
  if (stagger) {
    return (
      <Tag
        ref={ref}
        className={`reveal-stagger ${className}`}
        {...rest}
      >
        {React.Children.map(children, (child) => {
          if (!React.isValidElement(child)) return child;
          const childClass = `${child.props.className || ""} reveal ${
            visible ? "is-visible" : ""
          }`.trim();
          return React.cloneElement(child, { className: childClass });
        })}
      </Tag>
    );
  }

  return (
    <Tag
      ref={ref}
      className={`reveal ${visible ? "is-visible" : ""} ${className}`.trim()}
      {...rest}
    >
      {children}
    </Tag>
  );
};

export default Reveal;
